import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CreateIssueAssigneesSection} from '@github-ui/issue-metadata/CreateIssueAssigneesSection'
import {CreateIssueFieldsSection} from '@github-ui/issue-metadata/CreateIssueFieldsSection'
import {CreateIssueIssueTypesSection} from '@github-ui/issue-metadata/CreateIssueIssueTypesSection'
import {CreateIssueLabelsSection} from '@github-ui/issue-metadata/CreateIssueLabelsSection'
import {CreateIssueMilestonesSection} from '@github-ui/issue-metadata/CreateIssueMilestonesSection'
import {CreateIssueProjectsSection} from '@github-ui/issue-metadata/CreateIssueProjectsSection'
import {CreateIssueRelationshipsSection} from '@github-ui/issue-metadata/CreateIssueRelationshipsSection'

import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {useIssueCreateMetadata} from './../contexts/IssueCreateDataContext'
import {resolveIssueStatus} from './../utils/resolve-issue-status'
import {CreateIssueStatusSection} from './IssueStatusPicker'
import type {SharedMetadataProps} from './MetadataSelectors'
import styles from './MetadataSidebar.module.css'

export const MetadataSidebar = ({
  canSetAssignees,
  canSetLabels,
  canSetMilestone,
  canCreateLabel,
  canCreateMilestone,
  canSetProjects,
  canSetType,
  canSetIssueFields,
  canSetRelationships,
  issueTypesEnabled,
  ownerPinnedFields,
  isPinnedFieldsLoading,
  fieldVisibilityMap,
  ownerHasIssueFields,
  orgHasIssueTypes,
  openStatuses,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {
    repository,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
    issueStatus,
    setIssueStatus,
    setIssueFields,
    parentIssue,
    setParentIssue,
    blockedByIssues,
    setBlockedByIssues,
    blockingIssues,
    setBlockingIssues,
  } = useIssueCreateMetadata()

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
  }

  if (!repository) {
    return null
  }

  return (
    <div className={styles.MetadataSidebarContainer}>
      <h2 className="sr-only">Metadata</h2>
      {openStatuses.length > 0 && (
        <CreateIssueStatusSection
          statuses={openStatuses}
          status={resolveIssueStatus(openStatuses, issueStatus)}
          onSelectionChange={setIssueStatus}
        />
      )}
      {repository && (
        <CreateIssueAssigneesSection
          repo={repository.name}
          owner={repository.owner.login}
          readonly={!canSetAssignees}
          assignees={assignees}
          onSelectionChange={setAssignees}
          className={styles.CreateIssueAssigneesSection}
          repository={repository}
          issueNumber={undefined} // Not available in create context
          onIssueUpdate={undefined} // Not applicable in create context
          {...sharedConfigProps}
        />
      )}
      <CreateIssueLabelsSection
        repo={repository.name}
        owner={repository.owner.login}
        readonly={!canSetLabels}
        canCreateLabel={canCreateLabel}
        labels={labels}
        onSelectionChange={setLabels}
        {...sharedConfigProps}
      />
      {orgHasIssueTypes && issueTypesEnabled && (
        <CreateIssueIssueTypesSection
          owner={repository.owner.login}
          repo={repository.name}
          {...sharedConfigProps}
          viewerCanType={canSetType}
          type={issueType}
          onSelectionChange={setIssueType}
        />
      )}
      {ownerHasIssueFields && canSetIssueFields && (
        <CreateIssueFieldsSection
          ownerId={repository.owner.databaseId}
          onSelectionChange={setIssueFields}
          issueType={issueType}
          ownerPinnedFields={ownerPinnedFields}
          isPinnedFieldsLoading={isPinnedFieldsLoading}
          visibility={repository.visibility}
          isOwnerEnterpriseManaged={repository.isOwnerEnterpriseManaged ?? false}
          fieldVisibilityMap={fieldVisibilityMap}
          {...sharedConfigProps}
        />
      )}
      <CreateIssueProjectsSection
        owner={repository.owner.login}
        repo={repository.name}
        projects={projects}
        readonly={!canSetProjects}
        onSelectionChange={setProjects}
        {...sharedConfigProps}
      />
      <CreateIssueMilestonesSection
        repo={repository.name}
        owner={repository.owner.login}
        milestone={milestone}
        onSelectionChange={setMilestone}
        viewerCanSetMilestone={canSetMilestone ?? false}
        canCreateMilestone={canCreateMilestone}
        className={styles.CreateIssueAssigneesSection}
        {...sharedConfigProps}
      />
      {isFeatureEnabled('issue_create_relationships') && (
        <CreateIssueRelationshipsSection
          owner={repository.owner.login}
          repo={repository.name}
          readonly={!canSetRelationships}
          parentIssue={parentIssue}
          onParentChange={setParentIssue}
          blockedByIssues={blockedByIssues}
          onBlockedByChange={setBlockedByIssues}
          blockingIssues={blockingIssues}
          onBlockingChange={setBlockingIssues}
        />
      )}
    </div>
  )
}
